/**
 * The fold column — prototype H's right 440px: Day plan, Calendar · due,
 * Goals (thin bars + %), Habits · reviews (7-day cells + streak). All visible
 * at rest, all foldable to a ▸ header. Progressive disclosure without hiding
 * anything at rest (composition ticket 06: inline folds, never edge drawers).
 */

import type {
  BoardStateData,
  DefsGoal,
  DefsHabit,
  PlanItem,
} from '../types';
import type { UiState } from '../store';
import { checkbox, esc, hhmm, shiftDate, weekHeader } from './util';

export interface FoldsOptions {
  board: BoardStateData;
  ui: UiState;
  now: Date;
  /** id of the plan item currently in the Spotlight, if any. */
  spotlightRef?: string;
}

const DAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function renderFolds(o: FoldsOptions): string {
  return `<div class="side">
    ${fold('plan', 'Day plan', planCount(o), renderPlan(o), o.ui)}
    ${fold('shelf', 'Calendar · due', '', renderShelf(o), o.ui)}
    ${fold('goals', 'Goals', activeGoalCount(o), renderGoals(o), o.ui)}
    ${fold('habits', 'Habits · reviews', '', renderHabits(o), o.ui)}
  </div>`;
}

function fold(id: string, title: string, meta: string, body: string, ui: UiState): string {
  const open = ui.folds[id] ?? true;
  return `<section class="foldable ${open ? 'open' : ''}">
    <button class="summary" data-fold="${id}" aria-expanded="${open}">
      <span class="caret">${open ? '▾' : '▸'}</span> ${esc(title)}
      ${meta ? `<span class="m num">${esc(meta)}</span>` : ''}
    </button>
    ${open ? `<div class="foldbody">${body}</div>` : ''}
  </section>`;
}

/* ------------------------------------------------------------- Day plan */

function planCount(o: FoldsOptions): string {
  const plan = o.board.state.data?.plan;
  if (!plan) return 'not composed';
  const left = plan.items.filter((i) => i.checkedAt === null).length;
  return `${left} left`;
}

function renderPlan(o: FoldsOptions): string {
  const plan = o.board.state.data?.plan;
  if (!plan) {
    return `<div class="empty">Today isn't composed yet — the Spotlight is holding.</div>`;
  }
  if (plan.items.length === 0) {
    return `<div class="empty">Composed empty. A deliberately empty day still counts.</div>`;
  }
  return [...plan.items]
    .sort((a, b) => a.position - b.position)
    .map((item) => renderPlanRow(item, o))
    .join('');
}

function renderPlanRow(item: PlanItem, o: FoldsOptions): string {
  const done = item.checkedAt !== null;
  const key = `planItem:${item.id}`;
  const isNow = !done && o.spotlightRef === item.id;
  return `<div class="row ${isNow ? 'now-item' : ''}">
    ${checkbox('planItem', item.id, done, {
      pending: o.ui.pending.has(key),
      failed: o.ui.failed.has(key),
    })}
    <span class="name ${done ? 'done-name' : ''}">${esc(item.label)}</span>
    <span class="r">${esc(sourceTag(item))}</span>
  </div>`;
}

function sourceTag(item: PlanItem): string {
  switch (item.sourceKind) {
    case 'calendar':
      return 'cal';
    case 'reminder':
      return 'due';
    case 'next_action':
      return 'goal';
    case 'habit':
      return 'habit';
    default:
      return '';
  }
}

/* --------------------------------------------------------- Calendar · due */

function renderShelf(o: FoldsOptions): string {
  const events = [...(o.board.calendar.data.events ?? [])].sort(
    (a, b) => Date.parse(a.start) - Date.parse(b.start),
  );
  const due = o.board.reminders.data.due ?? [];
  const now = o.now.getTime();

  const cal = events.length
    ? events
        .map((e) => {
          const past = Date.parse(e.end) <= now;
          return `<div class="row">
            <span class="t num">${e.allDay ? 'all' : hhmm(e.start)}</span>
            <span class="name ${past ? 'done-name' : ''}">${esc(e.title)}</span>
          </div>`;
        })
        .join('')
    : `<div class="empty">No events today.</div>`;

  const rems = due.length
    ? due
        .map((r) => {
          const key = `reminder:${r.id}`;
          return `<div class="row">
            ${checkbox('reminder', r.id, r.done, {
              pending: o.ui.pending.has(key),
              failed: o.ui.failed.has(key),
            })}
            <span class="name ${r.done ? 'done-name' : ''}">${esc(r.title)}</span>
          </div>`;
        })
        .join('')
    : `<div class="empty">Nothing due.</div>`;

  return `${cal}<div class="subhead">Due</div>${rems}`;
}

/* ----------------------------------------------------------------- Goals */

function activeGoalCount(o: FoldsOptions): string {
  const n = o.board.defs.data.goals.filter((g) => g.status === 'active').length;
  return `${n} active`;
}

function renderGoals(o: FoldsOptions): string {
  const dirs = new Map(o.board.defs.data.directions.map((d) => [d.id, d]));
  const goals = o.board.defs.data.goals
    .filter((g) => g.status === 'active')
    .sort((a, b) => {
      const ra = dirs.get(a.directionId)?.rank ?? 99;
      const rb = dirs.get(b.directionId)?.rank ?? 99;
      return ra === rb ? a.due.localeCompare(b.due) : ra - rb;
    });
  if (!goals.length) {
    return `<div class="empty">No active Goals in defs/goals/ yet.</div>`;
  }
  return goals.map((g) => renderGoal(g, dirs.get(g.directionId)?.heading ?? g.directionId)).join('');
}

function renderGoal(g: DefsGoal, directionHeading: string): string {
  const pct = typeof g.progress === 'number' ? Math.round(g.progress * 100) : null;
  // No progress field exists in docs/defs-format.md — the bar renders only
  // when a blob supplies one (open question for Kevin).
  const bar =
    pct === null
      ? ''
      : `<span class="pct num">${pct}%</span><div class="gbar"><i style="width:${Math.max(0, Math.min(100, pct))}%"></i></div>`;
  return `<div class="g">
    <span class="gname">${esc(g.outcome)}</span>
    <span class="meta2">→ ${esc(directionHeading)} · ${esc(g.due)}</span>
    ${bar}
    <span class="meta2 next">next: ${esc(g.nextAction ?? '—')}</span>
  </div>`;
}

/* -------------------------------------------------------- Habits · reviews */

function renderHabits(o: FoldsOptions): string {
  const board = o.board;
  const habits = board.defs.data.habits;
  const state = board.state.data;
  const header = weekHeader(board.today)
    .map((d) => `<span>${d}</span>`)
    .join('');

  const rows = habits.length
    ? habits.map((h) => renderHabit(h, o)).join('')
    : `<div class="empty">No habits in defs/habits/ yet.</div>`;

  const reviews = state?.reviews;
  const reviewLine = reviews
    ? `daily · ${state?.plan ? `composed ${hhmm(state.plan.composedAt)}` : 'not composed'} · weekly ${reviewChip(
        reviews.weekly.dueOn,
        reviews.weekly.overdue,
        board.today,
      )} · quarterly ${reviewChip(reviews.quarterly.dueOn, reviews.quarterly.overdue, board.today)}`
    : 'reviews unavailable';

  return `<div class="dayhdr">${header}</div>${rows}<div class="g"><span class="meta2">${esc(reviewLine)}</span></div>`;
}

function renderHabit(h: DefsHabit, o: FoldsOptions): string {
  const st = o.board.state.data?.habits[h.id];
  const today = o.board.today;
  const checked = new Set(st?.checkedDates ?? []);
  const key = `habit:${h.id}`;
  const scheduledToday = isScheduled(h, today);
  const cells = Array.from({ length: 7 }, (_, i) => {
    const date = shiftDate(today, i - 6);
    const on = checked.has(date);
    const scheduled = isScheduled(h, date);
    return `<span class="c ${on ? 'on' : ''} ${scheduled ? '' : 'off-schedule'}" title="${date}"></span>`;
  }).join('');

  const control = scheduledToday
    ? checkbox('habit', h.id, st?.checkedToday ?? false, {
        pending: o.ui.pending.has(key),
        failed: o.ui.failed.has(key),
      })
    : `<span class="ck ck-na" title="not scheduled today">·</span>`;

  return `<div class="hb2">
    <div class="top">
      ${control}
      <span class="name ${st?.checkedToday ? 'done-name' : ''}">${esc(h.name)}</span>
      <span class="streak num">${st?.streak ?? 0}<small>d</small></span>
    </div>
    <div class="cells">${cells}</div>
  </div>`;
}

export function isScheduled(h: DefsHabit, date: string): boolean {
  if (h.schedule.type === 'daily') return true;
  const dow = new Date(Date.parse(`${date}T00:00:00Z`)).getUTCDay();
  return h.schedule.days.includes(DAY_CODES[dow]);
}

function reviewChip(dueOn: string, overdue: boolean, today: string): string {
  const days = Math.round((Date.parse(`${dueOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  if (overdue) return 'overdue';
  if (!Number.isFinite(days)) return '—';
  if (days === 0) return 'due today';
  if (days < 0) return `${-days}d past due`;
  return `in ${days}d`;
}
